import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import { useToolsApi } from '../apis/tools-apis';
import { Tool } from '../types';
import { useToast } from "../components/ui/Toast";
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { useNavigationGuard } from '../hooks/useNavigationGuard';
import ToolForm, { ToolFormRef } from './ToolForm';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_TOOL_TIMEOUT } from '../config/api-config';

export const AddToolPage: React.FC = () => {
  const formRef = useRef<ToolFormRef>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const toolsApi = useToolsApi();
  const { user } = useAuth();
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [existingToolNames, setExistingToolNames] = useState<string[]>([]);
  const backButtonClicked = useRef(false);
  const historyStateAdded = useRef(false);
  const [isSuccessfullySaved, setIsSuccessfullySaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [newTool, setNewTool] = useState<Tool>({
    toolId: '',
    toolName: '',
    toolDescription: '',
    toolUrlEndpoint: '',
    toolAuthentication: '',
    toolParameters: {
      parameters: [],
      testMode: true
    },
    toolOutput: {
      parameters: {
        output: {
          description: '',
          type: 'str'
        }
      }
    },
    toolAnnotation: '',
    ownerId: user?.sub || '',
    toolTimeout: DEFAULT_TOOL_TIMEOUT,
    toolTags: [],
    tags: [],
    isCore: false,
    isAuthPresent: false,
    accessControl: {
      viewAccessType: 'Owner Only',
      viewAllowedUsers: [],
      editAccessType: 'Owner Only',
      editAllowedUsers: []
    }
  });

  // Determine if there are unsaved changes by checking if any required fields are filled
  const hasUnsavedChanges = !isSuccessfullySaved && Boolean(
    newTool.toolName ||
    newTool.toolDescription ||
    newTool.toolUrlEndpoint ||
    newTool.toolAuthentication ||
    newTool.toolParameters.parameters.length > 0 ||
    newTool.toolOutput.parameters.output?.description ||
    newTool.toolAnnotation ||
    (newTool.toolTags && newTool.toolTags.length > 0)
  );

  const { 
    showNavigationWarning, 
    guardNavigation, 
    confirmNavigation, 
    cancelNavigation 
  } = useNavigationGuard(hasUnsavedChanges);

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      return (
        hasUnsavedChanges && 
        currentLocation.pathname !== nextLocation.pathname
      );
    }
  );

  // Handle blocked navigation
  useEffect(() => {
    if (blocker.state === "blocked") {
      guardNavigation(blocker.location.pathname);
    }
  }, [blocker, guardNavigation]);

  // Handle browser back button and page refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handlePopState = () => {
      if (hasUnsavedChanges) {
        guardNavigation('/studio/tools');
      } else {
        navigate('/studio/tools');
      }
    };
  
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, navigate]);

  useEffect(() => {
    const fetchExistingToolNames = async () => {
      try {
        const getAllTools = true;
        const tools = await toolsApi.getTools(getAllTools);
        setExistingToolNames(tools.map(tool => tool.toolName));
      } catch (error) {
        console.error('Error fetching existing tool names:', error);
        addToast({
          title: "Error",
          description: "Failed to fetch existing tool names",
          variant: "destructive",
          duration: 4000
        });
      }
    };

    fetchExistingToolNames();
  }, []);

  const handleSubmit = async () => {
    const isValid = await formRef.current?.validateForm();
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    setIsSaving(true);
  
    try {
      const createdTool = await toolsApi.createTool(newTool);
      setIsSuccessfullySaved(true);
      addToast({
        title: "Success",
        description: "Tool " + newTool.toolName + " has been successfully created",
        variant: "default",
        duration: 4000
      });
      
      // Navigate to the test page for the newly created tool
      navigate(`/studio/tools/test/${createdTool.toolId}`);
  
    } catch (error) {
      console.error('Error creating tool:', error);
      addToast({
        title: "Error",
        description: "Failed to create tool: " + String(error),
        variant: "destructive",
        duration: 8000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      guardNavigation('/studio/tools');
    } else {
      navigate('/studio/tools');
    }
  };

  const handleConfirmWithBlocker = () => {
    setIsSuccessfullySaved(true); // Reset the unsaved changes flag
    confirmNavigation();
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
  };

  const handleCancelWithBlocker = () => {
    cancelNavigation();
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  };

  return (
    <>
      <ToolForm
        ref={formRef}
        tool={newTool}
        onToolChange={(tool: Tool) => {
          setNewTool(tool);
          setIsSuccessfullySaved(false);
        }}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={false}
        setIsReadOnly={setIsReadOnly}
        existingToolNames={existingToolNames}
        currentUserId={user?.sub || ''}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmWithBlocker}
        onCancel={handleCancelWithBlocker}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default AddToolPage;